import { useState } from "react"
import styled from "styled-components"

import { Badge, Banner, Button, spacing } from "@minuthq/meatball-ui-react"

import { CreateTaskDialog } from "src/components/TaskManagement/CreateTaskDialog"
import { TaskListViewTab } from "src/components/TaskManagement/ListView/TaskListViewTab"
import { TaskCalendarView } from "src/components/TaskManagement/TaskCalendarView"
import { TaskTemplates } from "src/components/TaskManagement/templates/TaskTemplates"
import { HREF_MINUT_TASKS_FEEDBACK_FORM } from "src/constants/hrefs"
import { useFetchTasksCount } from "src/data/taskManagement/hooks/useTaskCount"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import FeedbackIcon from "src/ui/icons/feedback.svg"
import { MainView } from "src/ui/Layout/MainView"
import { MSkeleton } from "src/ui/MSkeleton/MSkeleton"
import { ITab } from "src/ui/Tabs/Tab"
import { TabPanel } from "src/ui/Tabs/TabPanel"
import { Tabs } from "src/ui/Tabs/Tabs"

import { TaskAutomations } from "./automations/TaskAutomations"

export function TaskManagementView() {
  const { t, langKeys } = useTranslate()
  const [openDialog, setOpenDialog] = useState(false)

  const { loading, error } = useFetchTasksCount({})

  return (
    <MainView
      title={
        <TitleWrapper>
          {t(langKeys.tasks)}
          <Badge>{t(langKeys.beta_access)}</Badge>
        </TitleWrapper>
      }
      titleBarProps={{
        description: t(langKeys.task_management_subheader),
        actionBar: <ShareFeedbackButton />,
      }}
    >
      <TasksContent loading={loading} isError={error} />

      {openDialog && <CreateTaskDialog onClose={() => setOpenDialog(false)} />}
    </MainView>
  )
}

function TasksContent({
  loading,
  isError,
}: {
  loading: boolean
  isError?: boolean
}) {
  const { t, langKeys } = useTranslate()

  const tabs = [
    {
      label: `${t(langKeys.task_management_list_view_tab_title)}`,
      id: Routes.ListView.location().hash ?? "list",
      view: <TaskListViewTab />,
    },
    {
      label: t(langKeys.task_management_calendar_view),
      id: Routes.CalendarTasks.location().hash ?? "calendar",
      view: <TaskCalendarView />,
    },
    {
      label: `${t(langKeys.template_plural)}`,
      id: Routes.TaskTemplates.location().hash!,
      view: <TaskTemplates />,
    },
    {
      label: `${t(langKeys.automation, { count: 2 })}`,
      id: Routes.TaskAutomations.location().hash!,
      view: <TaskAutomations />,
    },
  ] satisfies ITab[]

  if (loading) {
    return <TasksSkeleton />
  }

  if (isError) {
    return (
      <Banner type="error">
        {t(langKeys.failed_general_error_try_refreshing_page)}
      </Banner>
    )
  }

  return (
    <Tabs labels={tabs}>
      {tabs.map((tab) => (
        <StyledTabPanel key={tab.id}>{tab.view}</StyledTabPanel>
      ))}
    </Tabs>
  )
}

function TasksSkeleton() {
  return (
    <SkeletonWrapper>
      <MSkeleton variant="rect" height={30} />
      <MSkeleton variant="rect" height={30} />
      <MSkeleton variant="rect" height={30} />
    </SkeletonWrapper>
  )
}

function ShareFeedbackButton() {
  const { t, langKeys } = useTranslate()
  return (
    <Button
      // Used to find this button in amplitude
      data-engagement-id="task-management-feedback-button"
      size="small"
      variant="subtle"
      startIcon={<FeedbackIcon width={20} />}
      onClick={() => window.open(HREF_MINUT_TASKS_FEEDBACK_FORM, "_blank")}
    >
      {t(langKeys.feedback_action)}
    </Button>
  )
}

const StyledTabPanel = styled(TabPanel)`
  padding-top: ${spacing.XL2};
`

const SkeletonWrapper = styled.div`
  display: grid;
  gap: ${spacing.XS};
`

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.XS};
`
